import { useEffect, useRef, useState, type RefObject } from "react";
import clsx from "clsx";
import type { TaskDetail } from "@/services/api/agentic-v2";
import { TaskThread } from "../task-thread/TaskThread";
import { TASK_THREAD_SHELL_CLASS } from "../task-thread/taskThreadSurface";

/** Fixed panel width — inner shell stays this wide during width transitions. */
export const TASK_THREAD_DRAWER_WIDTH_PX = 440;

const TRANSITION_MS = 200;

/**
 * Slide-in drawer wrapping the shared TaskThread. Width animates from zero;
 * when open, chrome matches Mission Control (solid slate-700 shell like docked Ani).
 */
export interface TaskThreadDrawerProps {
  taskId: string | null;
  open: boolean;
  onClose: () => void;
  /** Forwarded to TaskThread so clicking the parent mission link works. */
  onOpenMission?: (missionId: string) => void;
  onTaskDeleted?: () => void;
  onTaskUpdated?: (task: TaskDetail) => void;
  /** Override default ``task-thread-drawer`` test id. */
  testId?: string;
  className?: string;
  /** When false, skip rounded corners on the open shell (e.g. ops rail). */
  rounded?: boolean;
}

/**
 * Mount heavy thread content after the width transition finishes opening;
 * keep it mounted until the close transition finishes. Swaps immediately when
 * the task id changes while already open.
 */
function useDeferredDrawerTaskId(
  open: boolean,
  taskId: string | null,
  asideRef: RefObject<HTMLElement | null>,
): string | null {
  const [renderTaskId, setRenderTaskId] = useState<string | null>(null);
  const renderTaskIdRef = useRef<string | null>(null);
  renderTaskIdRef.current = renderTaskId;

  useEffect(() => {
    const el = asideRef.current;
    const drawerOpen = Boolean(open && taskId);

    if (drawerOpen && taskId) {
      if (renderTaskIdRef.current !== null) {
        if (renderTaskIdRef.current !== taskId) {
          setRenderTaskId(taskId);
        }
        return undefined;
      }

      let cancelled = false;
      const mount = () => {
        if (!cancelled) setRenderTaskId(taskId);
      };
      const onEnd = (e: TransitionEvent) => {
        if (e.target !== el || e.propertyName !== "width") return;
        mount();
      };
      el?.addEventListener("transitionend", onEnd);
      const timer = window.setTimeout(mount, TRANSITION_MS + 24);
      return () => {
        cancelled = true;
        el?.removeEventListener("transitionend", onEnd);
        window.clearTimeout(timer);
      };
    }

    if (!drawerOpen && renderTaskIdRef.current !== null) {
      let cancelled = false;
      const unmount = () => {
        if (!cancelled) setRenderTaskId(null);
      };
      const onEnd = (e: TransitionEvent) => {
        if (e.target !== el || e.propertyName !== "width") return;
        unmount();
      };
      el?.addEventListener("transitionend", onEnd);
      const timer = window.setTimeout(unmount, TRANSITION_MS + 24);
      return () => {
        cancelled = true;
        el?.removeEventListener("transitionend", onEnd);
        window.clearTimeout(timer);
      };
    }

    return undefined;
  }, [open, taskId, asideRef]);

  return renderTaskId;
}

export function TaskThreadDrawer({
  taskId,
  open,
  onClose,
  onOpenMission,
  onTaskDeleted,
  onTaskUpdated,
  testId = "task-thread-drawer",
  className,
  rounded = true,
}: TaskThreadDrawerProps) {
  const asideRef = useRef<HTMLElement>(null);
  const drawerOpen = Boolean(open && taskId);
  const renderTaskId = useDeferredDrawerTaskId(open, taskId, asideRef);

  return (
    <aside
      ref={asideRef}
      data-testid={testId}
      className={clsx(
        "h-full flex-shrink-0 overflow-hidden transition-all duration-200 [contain:layout_paint]",
        drawerOpen ? clsx("w-[440px]", TASK_THREAD_SHELL_CLASS) : "w-0",
        drawerOpen && rounded && "rounded-2xl",
        className,
      )}
    >
      <div
        className="h-full"
        style={{ width: TASK_THREAD_DRAWER_WIDTH_PX }}
      >
        {renderTaskId ? (
          <TaskThread
            taskId={renderTaskId}
            onClose={onClose}
            onOpenMission={onOpenMission}
            onTaskDeleted={onTaskDeleted}
            onTaskUpdated={onTaskUpdated}
            variant="panel"
          />
        ) : null}
      </div>
    </aside>
  );
}
